import type {
  HazWasteCodeBasis,
  HazWasteDeterminationMethod,
  HazWasteDeterminationStatus,
  HazWasteGeneratorStatus,
  HazWastePhysicalState,
  HazWasteRegulatoryClass,
  HazWasteUnit,
} from '../../tri-shared/types/db.ts';
import type { HazWasteMaterialProgram } from '../types/api.ts';
import { EPA_WASTE_CODES } from '../waste-codes.ts';

export const HAZ_WASTE_PHYSICAL_STATES = [
  'solid',
  'liquid',
  'gas',
  'sludge',
] as const satisfies readonly HazWastePhysicalState[];

export const HAZ_WASTE_UNITS = [
  'lbs',
  'grams',
  'milligrams',
  'kg',
  'grains',
  'ounces',
  'tons',
  'cwt',
  'gallons',
  'liters',
  'cubicfeet',
  'fluidounces',
] as const satisfies readonly HazWasteUnit[];

export const HAZ_WASTE_DETERMINATION_METHODS = [
  '',
  'generator-knowledge',
  'testing',
  'sds-review',
  'other',
] as const satisfies readonly HazWasteDeterminationMethod[];

export const HAZ_WASTE_DETERMINATION_STATUSES = [
  'draft',
  'under-review',
  'active',
  'superseded',
  'archived',
] as const satisfies readonly HazWasteDeterminationStatus[];

/** The regulatory classes a material profile can carry: every class but hazardous waste. */
export const HAZ_WASTE_MATERIAL_PROGRAMS = [
  'used-oil',
  'universal-waste',
  'rcra-exempt-recyclable',
  'non-hazardous-solid-waste',
] as const satisfies readonly HazWasteMaterialProgram[];

export const HAZ_WASTE_REGULATORY_CLASSES = [
  'hazardous-waste',
  ...HAZ_WASTE_MATERIAL_PROGRAMS,
] as const satisfies readonly HazWasteRegulatoryClass[];

const ACUTE_F_LISTED_CODES = new Set(['F020', 'F021', 'F022', 'F023', 'F026', 'F027']);
const EPA_WASTE_CODE_SET = new Set(EPA_WASTE_CODES.map((wasteCode) => wasteCode.code));

export function normalizeEpaWasteCodes(codes: readonly string[]): string[] {
  return Array.from(new Set(codes.map((code) => code.trim().toUpperCase()).filter(Boolean))).sort();
}

export function invalidEpaWasteCodes(codes: readonly string[]): string[] {
  return normalizeEpaWasteCodes(codes).filter((code) => !EPA_WASTE_CODE_SET.has(code));
}

export function assertValidEpaWasteCodes(codes: readonly string[]): void {
  const invalidCodes = invalidEpaWasteCodes(codes);
  if (invalidCodes.length > 0) throw new Error(`Invalid EPA waste code(s): ${invalidCodes.join(', ')}.`);
}

/** Problems with a hazardous waste stream save request that can be found without the database. */
export function hazWasteStreamSaveRequestErrors(request: {
  name: string;
  determinationStatus: string;
  epaWasteCodes: readonly string[];
  determinationMethod: string;
}): string[] {
  const errors: string[] = [];
  if (!request.name.trim()) errors.push('Waste streams must have a name.');
  if (request.determinationStatus === 'archived') errors.push('Use the archive action to archive waste streams.');
  const invalidCodes = invalidEpaWasteCodes(request.epaWasteCodes);
  if (invalidCodes.length > 0) errors.push(`Invalid EPA waste code(s): ${invalidCodes.join(', ')}.`);
  if (request.determinationStatus === 'active') {
    if (normalizeEpaWasteCodes(request.epaWasteCodes).length === 0)
      errors.push('Active hazardous waste streams must include at least one EPA waste code.');
    if (request.determinationMethod === '')
      errors.push('Active hazardous waste streams must include a determination method.');
  }
  return errors;
}

/** A material profile's primary program, with the parts of that program's details an active profile requires. The
 * details are null while they have not been filled in yet. */
type MaterialProfileProgramRequirements =
  | {
      primaryProgram: 'used-oil';
      detail: {
        sourceDescription: string;
        halogenMeasurement: { totalHalogensPpm: number; basis: string } | null;
      } | null;
    }
  | { primaryProgram: 'universal-waste'; detail: { category: string; accumulationDateMethod: string } | null }
  | {
      primaryProgram: 'non-hazardous-solid-waste';
      detail: { category: string; stateOrLocalClassification: string; intendedManagement: string } | null;
    }
  | { primaryProgram: 'rcra-exempt-recyclable'; detail: { citation: string; factualRationale: string } | null };

function activeMaterialProgramError(program: MaterialProfileProgramRequirements): string | null {
  switch (program.primaryProgram) {
    case 'used-oil':
      return !program.detail?.sourceDescription.trim() || program.detail.halogenMeasurement === null
        ? 'Active used-oil material profiles must include source details and a halogen determination basis.'
        : null;
    case 'universal-waste':
      return !program.detail?.category.trim() || !program.detail.accumulationDateMethod.trim()
        ? 'Active universal-waste material profiles must include a category and accumulation-date tracking method.'
        : null;
    case 'non-hazardous-solid-waste':
      return !program.detail?.category.trim() ||
        !program.detail.stateOrLocalClassification.trim() ||
        !program.detail.intendedManagement.trim()
        ? 'Active non-hazardous material profiles must include category, state/local classification, and intended management.'
        : null;
    case 'rcra-exempt-recyclable':
      return !program.detail?.citation.trim() || !program.detail.factualRationale.trim()
        ? 'Active exempt or recyclable material profiles must include an exemption claim citation and factual rationale.'
        : null;
  }
}

/** Problems with a material profile save request that can be found without the database. */
export function materialProfileSaveRequestErrors(request: {
  name: string;
  determinationStatus: string;
  determinationMethod: string;
  program: MaterialProfileProgramRequirements;
}): string[] {
  const errors: string[] = [];
  if (!request.name.trim()) errors.push('Material profiles must have a name.');
  if (request.determinationStatus === 'archived') errors.push('Use the archive action to archive material profiles.');
  if (request.determinationStatus === 'active') {
    if (request.determinationMethod === '')
      errors.push('Active material profiles must include a determination method.');
    const programError = activeMaterialProgramError(request.program);
    if (programError) errors.push(programError);
  }
  return errors;
}

export function deriveWasteCodeBasis(codes: readonly string[]): HazWasteCodeBasis {
  const normalized = normalizeEpaWasteCodes(codes);
  assertValidEpaWasteCodes(normalized);
  const hasCharacteristic = normalized.some((code) => /^D\d{3}$/.test(code));
  const hasListed = normalized.some((code) => /^[FKPU]\d{3}$/.test(code));
  if (hasCharacteristic && hasListed) return 'both';
  if (hasCharacteristic) return 'characteristic';
  return 'listed';
}

export function isAcuteHazardousWasteCode(code: string): boolean {
  const normalized = code.trim().toUpperCase();
  return /^P\d{3}$/.test(normalized) || ACUTE_F_LISTED_CODES.has(normalized);
}

export function isAcuteHazardousFromCodes(codes: readonly string[]): boolean {
  return codes.some(isAcuteHazardousWasteCode);
}

/** Federal monthly generator category thresholds from 40 CFR 262.13 Table 1. */
export function computeHazWasteGeneratorStatus(
  totalNonAcuteKg: number,
  totalAcuteKg: number,
  totalAcuteSpillCleanupResidueKg: number,
): HazWasteGeneratorStatus {
  if (totalAcuteKg > 1 || totalNonAcuteKg >= 1000 || totalAcuteSpillCleanupResidueKg > 100) return 'lqg';
  if (totalNonAcuteKg > 100) return 'sqg';
  if (totalNonAcuteKg > 0 || totalAcuteKg > 0 || totalAcuteSpillCleanupResidueKg > 0) return 'vsqg';
  return 'not-generator';
}
